import {
  Calendar,
  MessageSquare,
  Paperclip,
  MoreVertical,
  ChevronDown,
  ChevronUp,
  MapPin,
  FileText,
} from "lucide-react";
import { motion } from "motion/react";
import { useDrag } from "react-dnd";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TicketInterface } from "../../../../models/TicketModule/Ticket";
import { lightenColor } from "./KanbanColumn";
import { useLanguageStore } from "../../../../store/useLanguageStore";
import FeedbackModal from "../../Tickets/TicketModal/FeedbackModal";
import { TicketImageInterface } from "../../../../models/TicketModule/TicketImage";
import { ImageGalleryModal } from "./ImageGalleryModal";
import ticketService from "../../../../services/ticketService/ticketService";
import useUpdateKanbanTicket from "../../../../store/ReactQueries/tickets/useUpdateKanbanTicket";

interface TicketCardProps {
  ticket: TicketInterface;
  onHeightChange?: () => void;
}

const avatarColors = [
  "bg-slate-200 text-slate-700",
  "bg-zinc-200 text-zinc-700",
  "bg-stone-200 text-stone-700",
  "bg-gray-200 text-gray-700",
  "bg-neutral-200 text-neutral-700",
];

function TicketCardComponent({ ticket, onHeightChange }: TicketCardProps) {
  const { language } = useLanguageStore();
  const [openFeedback, setOpenFeedback] = useState<boolean>(false);

  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const [openGallery, setOpenGallery] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const [openDuePicker, setOpenDuePicker] = useState(false);
  const [dueDraft, setDueDraft] = useState<string>(() => {
    if (!ticket.dueDate) return "";
    const d = new Date(ticket.dueDate);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });

  const dateRef = useRef<HTMLInputElement | null>(null);

  const updateTicket = useUpdateKanbanTicket();
  const isSavingDueDate = updateTicket.isPending;

  useEffect(() => {
    if (!ticket.dueDate) {
      setDueDraft("");
      return;
    }
    const d = new Date(ticket.dueDate);
    setDueDraft(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
    );
  }, [ticket.dueDate]);

  useEffect(() => {
    if (!openDuePicker) return;

    const onDocClick = () => setOpenDuePicker(false);
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [openDuePicker]);

  const ticketImages = useMemo(() => {
    const imgs = (ticket as any)?.images as TicketImageInterface[] | undefined;
    return Array.isArray(imgs) ? imgs.filter((x) => !!x?.imageUrl) : [];
  }, [ticket]);

  const imagesCount = ticketImages.length;

  const openImages = useCallback(
    (startAt = 0) => {
      if (!imagesCount) return;
      setGalleryIndex(Math.min(Math.max(startAt, 0), imagesCount - 1));
      setOpenGallery(true);
    },
    [imagesCount]
  );

  const closeImages = useCallback(() => setOpenGallery(false), []);

  useEffect(() => {
    if (!onHeightChange) return;
    const id = requestAnimationFrame(() => onHeightChange());
    return () => cancelAnimationFrame(id);
  }, [isExpanded, onHeightChange]);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: "TICKET",
      item: { id: ticket.id },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [ticket.id]
  );

  const toggleExpanded = useCallback(() => setIsExpanded((v) => !v), []);

  const formatDate = useCallback(
    (date: Date | undefined) => {
      if (!date) return "N/A";

      const dateObj = new Date(date);
      const today = new Date();
      const diffTime = dateObj.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return `${language["Today"]}`;
      if (diffDays === 1) return `${language["tomorrow"]}`;
      if (diffDays === -1) return `${language["yesterday"]}`;
      if (diffDays < 0) return `${Math.abs(diffDays)} ${language["daysLate"]}`;

      return dateObj.toLocaleDateString("sq-AL", { day: "numeric", month: "short" });
    },
    [language]
  );

  const getInitials = useCallback((firstname: string, lastname: string) => `${firstname[0]}${lastname[0]}`, []);

  const getAvatarColor = useCallback((name: string) => {
    const hash = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return avatarColors[hash % avatarColors.length];
  }, []);

  const isOverdue = useMemo(() => {
    if (!ticket.dueDate) return false;
    const dueDate = new Date(ticket.dueDate);
    const today = new Date();
    return dueDate < today && !ticket.status?.isCompleted;
  }, [ticket.dueDate, ticket.status?.isCompleted]);

  const priorityLabel = ticket.priority?.title || "Normal";
  const priorityColor = ticket.priority?.badgeColor || "#F3F4F6";

  const assigneeName = useMemo(
    () => (ticket.assignedTo ? `${ticket.assignedTo.firstname} ${ticket.assignedTo.lastname}` : "Unassigned"),
    [ticket.assignedTo?.firstname, ticket.assignedTo?.lastname]
  );

  const priorityBg = useMemo(() => lightenColor(priorityColor, 0.85), [priorityColor]);

  const closeDuePicker = useCallback(() => setOpenDuePicker(false), []);

  const saveDueDate = useCallback(() => {
    if (!ticket.id) return;
    if (isSavingDueDate) return;

    if (!dueDraft) {
      closeDuePicker();
      return;
    }

    const newDueDate = new Date(`${dueDraft}T00:00:00`);

    updateTicket.mutate(
      { ticketId: ticket.id, patch: { ...ticket, dueDate: newDueDate } },
      { onSuccess: () => closeDuePicker() }
    );
  }, [ticket.id, dueDraft, closeDuePicker, updateTicket, isSavingDueDate]);

  return (
    <>
      <motion.div
        ref={drag}
        className={`group bg-white rounded-xl border border-gray-200 cursor-grab active:cursor-grabbing overflow-hidden ${
          isHovered ? "shadow-lg border-gray-300" : "shadow-sm hover:shadow-md"
        }`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        animate={{
          opacity: isDragging ? 0.5 : 1,
          scale: isDragging ? 0.95 : 1,
          rotate: isDragging ? 3 : 0,
          boxShadow: isDragging
            ? "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
            : "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
        }}
        whileHover={{
          scale: isDragging ? 0.95 : 1.02,
          transition: { duration: 0.2 },
        }}
        whileTap={{ scale: 0.98 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 25,
        }}
      >
        {/* Card Content */}
        <div className="p-3.5">
          {/* Title */}
          <div className="flex items-start justify-between gap-2 mb-2.5">
            <h4 className="text-[13px] text-gray-900 leading-snug flex-1 font-medium">{ticket.title}</h4>
            <button className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-50 rounded">
              <MoreVertical className="w-3.5 h-3.5 text-gray-400" />
            </button>
          </div>

          {/* Priority & Due Date Row */}
          <div className="flex items-center gap-2 mb-2.5 relative">
            <span
              className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md font-medium"
              style={{ color: priorityColor, backgroundColor: priorityBg }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: priorityColor }} />
              {priorityLabel}
            </span>

            {/* CLICKABLE DATE */}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpenDuePicker((v) => !v);
              }}
              className={`flex items-center gap-1 text-[11px] font-medium rounded px-1.5 py-1 transition
              ${isOverdue ? "text-red-600" : "text-gray-500"} hover:bg-gray-50`}
              title="Change due date"
            >
              <Calendar className="w-3 h-3" />
              <span>{formatDate(ticket.dueDate)}</span>
            </button>

            {/* POPOVER */}
            {openDuePicker && (
              <div
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className="absolute z-50 top-12 left-[23px] bg-white border border-gray-200 rounded-lg shadow-lg p-2.5 w-[220px]"
                style={{ top: "-7px" }}
              >
                <div className="text-[10px] font-semibold text-gray-600 mb-2">{language["DueDate"] || "Due date"}</div>

                <div className="relative">
                  <input
                    ref={dateRef}
                    type="date"
                    value={dueDraft}
                    onChange={(e) => setDueDraft(e.target.value)}
                    onClick={() => {
                      (dateRef.current as any)?.showPicker?.();
                    }}
                    className="w-full border border-gray-200 rounded-md pl-2 pr-4 py-1.5 text-[12px] outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>

                <div className="flex items-center justify-end gap-2 mt-2">
                  <button
                    type="button"
                    onClick={closeDuePicker}
                    disabled={isSavingDueDate}
                    className="px-2.5 py-1.5 text-[12px] rounded-md hover:bg-gray-50 text-gray-600 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {language["Cancel"] || "Cancel"}
                  </button>
                  <button
                    type="button"
                    onClick={saveDueDate}
                    disabled={isSavingDueDate}
                    className={`px-2.5 py-1.5 text-[12px] rounded-md bg-blue-600 text-white hover:bg-blue-700
    ${isSavingDueDate ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    {isSavingDueDate ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="w-3.5 h-3.5 rounded-full border-2 border-white/70 border-t-transparent animate-spin" />
                      </span>
                    ) : (
                      language["Save"] || "Save"
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 mb-2.5 flex-wrap">
            <div className="flex items-center gap-1 text-[10px] text-gray-500">
              <MapPin className="w-3 h-3 opacity-60" />
              <span>{ticket.location?.title || "N/A"}</span>
            </div>
            {ticket.category?.title && (
              <>
                <span className="text-gray-300">•</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 font-medium">
                  {ticket.category.title}
                </span>
              </>
            )}
          </div>

          <div className="flex items-center justify-between pt-2.5 border-t border-gray-100">
            <div className="flex items-center gap-1.5">
              {ticket.assignedTo ? (
                <>
                  <div
                    className={`w-6 h-6 rounded-full ${getAvatarColor(
                      assigneeName
                    )} flex items-center justify-center text-[9px] font-semibold`}
                  >
                    {getInitials(ticket.assignedTo.firstname, ticket.assignedTo.lastname)}
                  </div>
                  <span className="text-[10px] text-gray-600 font-medium">{assigneeName}</span>
                </>
              ) : (
                <span className="text-[10px] text-gray-400 italic">{language["NotAssigned"]}</span>
              )}
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setOpenFeedback(true)}
                className="flex items-center gap-1 px-2 py-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-200 cursor-pointer group/chat"
              >
                <MessageSquare className="w-4 h-4 group-hover/chat:scale-110 transition-transform" />
              </button>

              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openImages(0);
                }}
                disabled={!imagesCount}
                className={`flex items-center gap-1 px-2 py-1 rounded-md transition-all duration-200 cursor-pointer group/attach
                  ${
                    imagesCount
                      ? "text-gray-400 hover:text-purple-600 hover:bg-purple-50"
                      : "text-gray-300 cursor-not-allowed opacity-60"
                  }`}
                title={imagesCount ? "Open images" : "No images"}
              >
                <Paperclip className="w-4 h-4 group-hover/attach:scale-110 transition-transform" />
                {imagesCount > 0 && <span className="text-[11px] font-semibold">{imagesCount}</span>}
              </button>

              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleExpanded();
                }}
                className={`flex items-center gap-0.5 px-2 py-1 rounded-md transition-all duration-200 ${
                  isExpanded ? "text-gray-700 bg-gray-100" : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                }`}
              >
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <motion.div
            initial={false}
            animate={{ height: isExpanded ? "auto" : 0, opacity: isExpanded ? 1 : 0 }}
            transition={{
              height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
              opacity: { duration: 0.25, ease: [0.4, 0, 0.2, 1] },
            }}
            onAnimationComplete={() => onHeightChange?.()}
            className="overflow-hidden"
          >
            <div className="pt-3 mt-2.5 border-t border-gray-100">
              <div className="flex items-start gap-2 mb-2">
                <div className="p-1.5 bg-indigo-50 rounded-md">
                  <FileText className="w-3.5 h-3.5 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <span className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide block mb-1">
                    {language["Description"]}
                  </span>
                  <p className="text-[12px] text-gray-700 leading-relaxed">{ticket.description}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {openFeedback && (
          <FeedbackModal open={openFeedback} ticketId={ticket.id || ""} handleClose={() => setOpenFeedback(false)} />
        )}
      </motion.div>

      <ImageGalleryModal open={openGallery} images={ticketImages} startIndex={galleryIndex} onClose={closeImages} />
    </>
  );
}

export const TicketCard = memo(TicketCardComponent, (prev, next) => {
  if (prev.onHeightChange !== next.onHeightChange) return false;

  const a = prev.ticket;
  const b = next.ticket;

  return (
    a.id === b.id &&
    a.statusId === b.statusId &&
    a.title === b.title &&
    a.description === b.description &&
    a.dueDate === b.dueDate &&
    a.assignedToId === b.assignedToId &&
    a.priority?.title === b.priority?.title &&
    a.priority?.badgeColor === b.priority?.badgeColor &&
    a.location?.title === b.location?.title &&
    a.category?.title === b.category?.title &&
    a.status?.isCompleted === b.status?.isCompleted &&
    ((a as any)?.images?.length ?? 0) === ((b as any)?.images?.length ?? 0)
  );
});
